<script>
import {mapGetters} from 'vuex'
import {OrderStatus} from '@/enums/OrderStatus'
import {WizardSteps} from '@/enums/WizardSteps'
import {formatter} from '@/helpers/formatter'
import {getOrdersTableHeaders} from '@/components/orders/OrdersTableHeaders'
import OrderDocumentsDialog from '@/components/orders/OrderDocumentsDialog.vue'
import OrderCancellationDialog from '@/components/orders/OrderCancellationDialog.vue'

export default {
  components: {OrderCancellationDialog, OrderDocumentsDialog},
  mixins: [formatter],
  props: {
    orders: {type: Array},
    deleteOrder: {type: Function},
    downloadInvoice: {type: Function},
    fetchOrders: {type: Function} // comes from OrdersList
  },
  data: (vm) => ({
    isOrderDocumentsDialogVisible: false,
    isOrderCancellationDialogVisible: false,
    showOrganization: (vm.$store.getters['organizations'] || []).length > 1,
    dialogHeader: '',
    headers: [],
    orderStatus: OrderStatus,
    selectedOrder: null,
    updatedEvent: null,
    search: ''
  }),
  computed: {
    ...mapGetters(['locale', 'selectedOrganization']),
    isMobile: function () {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return true
        default:
          return false
      }
    },
  },
  watch: {
    isOrderDocumentsDialogVisible(dialogState, dialogStateOld) {
      // refresh when dialog was closed
      // and minimum 1 document was deleted
      if (dialogStateOld && !dialogState) {
        if (this.updatedEvent !== null) this.updatedEvent.source = 'Dialog'
        this.$emit('update:updated', this.updatedEvent)
      }
    }
  },
  created: function () {
    this.headers = getOrdersTableHeaders(
      this.$permissions,
      this.showOrganization
    )
  },
  methods: {
    openOrderDocumentsDialog(order) {
      this.selectedOrder = order
      this.isOrderDocumentsDialogVisible = true
    },
    closeOrderDocumentsDialog() {
      this.isOrderDocumentsDialogVisible = false
    },
    openOrderCancellationDialog(order) {
      this.selectedOrder = order
      this.isOrderCancellationDialogVisible = true
    },
    closeOrderCancellationDialog() {
      this.isOrderCancellationDialogVisible = false
    },
    goToWizard(order) {
      let locale = this.locale
      this.$router.push({
        name: 'uploadParams',
        params: {
          step: WizardSteps[order.orderStatus],
          orderId: order.orderId,
          locale: locale
        }
      })
    },
    async onRequestCancelOrder(order) {
      let response = await this.$ordersService.requestOrderCancellation(order?.orderId)
      return response && this.onCancelOrder(order)
    },
    onCancelOrder(order) {
      const documentIds = order?.orderPositions?.map((position) => {
        return position.uploadFileId
      })
      return this.$documentsService.cancelDocuments(documentIds)
    },
    onDownloadInvoice(order) {
      this.downloadInvoice(order.orderId, order.orderNumber)
    },
    onDeleteOrder(order) {
      this.deleteOrder(order.orderId)
    },
    exportCSV(item) {
      this.$ordersService.exportCSV(item.orderId, item.organizationId)
      .then(response => {
        this.$ordersService.clientCSVDownload(response, item.orderId)
      })
      .catch((error) => {
        this.$notify(this.$t('component.orders.ordersTable.csv.export.error'), {type:'error'})
        console.error('Error exporting CSV', error)
      })
    }
  }
}
</script>
<template>
  <div>
    <OrderDocumentsDialog
      :selected-order="selectedOrder"
      :is-visible="isOrderDocumentsDialogVisible"
      @close="closeOrderDocumentsDialog" />
    <OrderCancellationDialog
      :selected-order="selectedOrder"
      :is-visible="isOrderCancellationDialogVisible"
      @cancelled="fetchOrders"
      @close="closeOrderCancellationDialog" />
    <v-data-table
      :header-props="{
        sortByText: $t('global.sort.by.text')
      }"
      :footer-props="{
        'items-per-page-text': $t('global.items.per.page.text'),
        'items-per-page-all-text': $t('global.items.per.page.all.text'),
        'page-text': $t('global.page.text'),
        'items-per-page-options': [10, 25, 50, 100, -1]
      }"
      :headers="headers"
      :items.sync="orders"
      :items-per-page="25"
      class="elevation-1"
      :height="500"
      :custom-sort="$ordersService.customSort"
      fixed-header
      :class="{ mobile: isMobile }"
      item-key="orderNumber"
      dense
      :search="search"
      :no-results-text="$t('global.no.data.text')"
      calculate-widths>
      <template #no-data>
        {{ $t('global.no.entries') }}
      </template>
      <template #top>
        <v-text-field
          id="search"
          v-model="search"
          :label="$t('global.search')"
          class="mx-4"
        ></v-text-field>
      </template>
      <template #[`item.orderStatus`]="{ item }">
            <span>
              {{$t('component.orders.ordersTable.status.' + orderStatus.getKey(item.orderStatus))}}
            </span>
        <span v-if="item.orderSchedule && item.orderStatus !== 700 ">
              <br/>
               (<b>{{momentDate(item.orderSchedule.autocompletionDate).format('dddd, DD.MM.YYYY HH:mm:ss')}}</b>)
             </span>
        <v-tooltip
          v-if="item.orderStatus === orderStatus.paymentRefused"
          top
          color="black">
          <template #activator="{ on }">
            <v-icon class="ml-1" color="secondary" size="medium" v-on="on">
              mdi-information-outline
            </v-icon>
          </template>
          <span>{{ $t('component.orders.ordersTable.payment.refused.tooltip') }}</span>
        </v-tooltip>
      </template>
      <template #[`item.orderNumber`]="{ item }">
        <v-card flat color="transparent">
          <v-card-title class="pl-0 pr-0 pt-2">
            <h4 class="primary--text text-no-wrap">
              {{ item.orderNumber }}
            </h4>
          </v-card-title>
          <v-card-subtitle name="table-document-count" class="pl-0 pr-0 pb-2">
            <v-tooltip
              v-if="$ordersService.canShowDetails(item) && !isMobile"
              slot="prepend"
              top
              color="black">
              <template #activator="{ on }">
                <v-icon
                  :name="'showDocumentList'"
                  color="secondary"
                  size="medium"
                  v-on="on"
                  @click="openOrderDocumentsDialog(item)">
                  mdi-alert-circle-outline
                </v-icon>
              </template>
              <span>{{ $t('component.orders.ordersTable.document.count.tooltip') }}</span>
            </v-tooltip>
            {{ $t('component.orders.ordersTable.document.count.table') }}
            {{ item.documentCount }}
          </v-card-subtitle>
        </v-card>
      </template>
      <template #[`item.organizationId`]="{item}">
            <span>{{ $organizationService.getOrganizationNameById(item.organizationId) }}</span>
      </template>
      <template #[`item.owner.lastName`]="{ item }">
            <span>
              {{ item.owner ? item.owner.lastName : '' }}</span>
      </template>
      <template #[`item.dataSource`]="{ item }">
        <v-chip
          v-if="item.dataSource"
          small>
          {{ item.dataSource }}
        </v-chip>
      </template>
      <template #[`item.orderCreateDateTime`]="{ item }">
            <span>{{
                formatShortDate(item.orderCreateDateTime)
              }}
            </span>
      </template>
      <template #[`item.orderPrice`]="{ item }">
            <span class=" d-block ">{{
                item.orderPrice.totalNetPrice === 0
                  ? '-'
                  : formatCurrency(item.orderPrice.totalNetPrice)
              }}</span>
        <span
          v-if="item.orderPrice.totalGrossPrice > 0"
          class="caption "
          v-html="$t('component.orders.ordersTable.table.column.gross.price')
                  .replace(
                    '{{GrossPrice}}',
                    formatCurrency(item.orderPrice.totalGrossPrice) )">
            </span>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-btn
          v-if="$ordersService.canEdit(item)"
          color="primary"
          depressed
          small
          @click="goToWizard(item)">
          {{ $t('component.orders.ordersTable.table.action.next.step') }}
        </v-btn>
        <v-menu bottom left>
          <template #activator="{ on }">
            <v-btn
              name="actions"
              depressed
              tile
              small
              color="primary"
              width="35"
              min-width="0"
              class="ml-2"
              v-on="on">
              <v-icon>mdi-menu</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-if="$ordersService.canShowDetails(item)"
              name="details"
              @click="openOrderDocumentsDialog(item)">
              {{ $t('component.orders.ordersTable.table.action.details') }}
            </v-list-item>
            <v-list-item
              v-if="$ordersService.canDownloadInvoice(item)"
              name="invoice"
              @click="onDownloadInvoice(item)">
              {{ $t('component.orders.ordersTable.table.action.invoice') }}
            </v-list-item>
            <v-list-item
              v-if="$ordersService.canDeleteOrder(item)"
              color="error"
              name="delete"
              @click="onDeleteOrder(item)">
              {{ $t('component.orders.ordersTable.table.action.delete') }}
            </v-list-item>
            <v-list-item
              v-if="$ordersService.canCancelOrder(item)"
              color="error"
              name="delete"
              @click="openOrderCancellationDialog(item)">
              {{ $t('component.orders.ordersTable.table.action.cancel') }}
            </v-list-item>
            <v-list-item
              color="error"
              name="delete"
              @click="exportCSV(item)">
              {{ $t('component.orders.ordersTable.csv.export') }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
  </div>
</template>
<style lang="scss">
.mobile table {
  border-collapse: collapse;
}
.mobile .v-data-table__wrapper tbody tr:nth-child(odd) {
  border-left: 6px solid var(--v-primary-base);
}
.mobile .v-data-table__wrapper tbody tr:nth-child(even) {
  border-left: 6px solid var(--v-error-base);
}
</style>
